import React from "react";
import './single_example.css';
import { observer } from "mobx-react";
import { state } from "./state"; // Import your MobX store
import * as color_utils from "./color_utils";
import SingleExampleWordtree from './single_example_wordtree';
import SingleExampleHighlights from "./single_example_highlights";
import SingleExampleWordGraph from "./single_example_wordgraph";
import SingleExampleWordGraphUntangle from "./single_example_wordgraph_untangle";
import SingleExampleTimeCurves from "./single_example_time_curves";
import { reaction } from 'mobx';
import { telemetry } from "./telemetry";
import { urlParams, URLParam, type VisType } from "./url_params_manager";


class SingleExample extends React.Component {
    state = {
        promptGroups: [] as { promptId: string, generations: string[] }[]
    };
    disposer?: () => void;
    private hasLoggedListLoadRef = false;

    private getPromptGroupKey(prompt: any, originalIndex: number): string {
        return `${prompt.text}\x01${originalIndex}\x01${prompt.modelFamily}_${prompt.model}`;
    }

    render() {
        if (!state.prompts.length || !state.prompts[0]?.text) {
            return;
        }
        return (
            <div className="single-example">
                <div>
                    {this.renderOutputs()}
                </div>
            </div>
        );
    }

    renderOutputs() {
        if (!this.state.promptGroups) {
            return;
        }

        let vis;
        let instructionText = '';
        switch (state.visType) {
            case 'word_tree':
                vis = this.renderOutputsWordTree();
                instructionText = `Hover over a word to highlight it across all generated sentences. 
                The size of the word corresponds to its frequency in the generated sentences.`
                break;
            case 'graph':
                vis = this.renderOutputsGraph();
                instructionText = `Hover over any word or phrase to highlight all sentences containing it. 
                \nClick to pin the highlight - click again or click elsewhere to unpin.
                \nDrag to pan, scroll to zoom in and out.`
                break;
            case 'graph_untangle':
                vis = this.renderOutputsGraphUntangle();
                instructionText = `Hover over any word or phrase to highlight all sentences containing it. 
                \nUse the Untangle slider to switch between graph view and table view.
                \nDrag to pan, scroll to zoom in and out.`
                break;
            case 'time_curves':
                vis = this.renderOutputsTimeCurves();
                instructionText = 'Time curves visualization of LLM outputs.';
                break;
            case 'highlights':
                vis = this.renderOutputsHighlights();
                instructionText = 'Highlighted words are the ones that are present in multiple generated sentences. The bolded output is the text you entered to compare against the LLM outputs (if provided). Color of highlights is not related to the colors used in the graph visualization.'
                break;
            case 'first_output':
                vis = this.renderOutputsBasic(true);
                instructionText = 'First output generated by the LLM for each prompt.'
                break;
            default:
                vis = this.renderOutputsBasic();
                instructionText = 'Outputs generated by the LLM.'

        }
        const visualizationOptions = [
            { value: 'graph', label: 'Graph' },
            { value: 'graph_untangle', label: 'Graph (Untangle)' },
            { value: 'time_curves', label: 'Time Curves' },
            { value: 'raw_outputs', label: 'Raw Outputs' },
            { value: 'first_output', label: 'First Output' },
            ...(!state.isUserStudy ? [
                { value: 'word_tree', label: 'Word Tree' },
                { value: 'highlights', label: 'Highlights' },
            ] : []),
        ];

        return (<div>
            <div className="section-label-with-selector">
                <span className="section-label-text">LLM Generations:</span>
                {!state.isUserStudy && (
                    <select
                        id="vis-type-select"
                        value={state.visType}
                        onChange={(e) => {
                            const visType = e.target.value as VisType;
                            state.setVisType(visType);
                            telemetry.logVisTypeChange(visType);
                        }}
                    >
                        {visualizationOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                )}
            </div>
            {/* <div className="instruction-text">
                {instructionText}
            </div> */}
            {vis}
        </div>)
    }


    renderOutputsWordTree() {
        const firstGenerations = this.state.promptGroups[0]?.generations;
        return <SingleExampleWordtree 
            generations={firstGenerations}
        ></SingleExampleWordtree>;
    }
    renderOutputsGraph() {
        return <SingleExampleWordGraph 
            promptGroups={this.state.promptGroups}
        ></SingleExampleWordGraph>;
    }
    renderOutputsGraphUntangle() {
        return <SingleExampleWordGraphUntangle 
            promptGroups={this.state.promptGroups}
        ></SingleExampleWordGraphUntangle>;
    }
    renderOutputsTimeCurves() {
        return <SingleExampleTimeCurves 
            promptGroups={this.state.promptGroups}
        ></SingleExampleTimeCurves>;
    }

renderOutputsBasic(firstOnly: boolean = false) {
    if (!this.state.promptGroups || this.state.promptGroups.length === 0) {
        return <div className="outputs">No outputs available</div>;
    }

    const isUserStudyTwoPrompts = state.isUserStudy && this.state.promptGroups.length === 2;
    const outputsClassName = `outputs${isUserStudyTwoPrompts ? ' outputs-user-study-two-prompts' : ''}`;

    return (
        <div className={outputsClassName}>
            {this.state.promptGroups.map((group, groupIndex) => {
                const originalIndex = color_utils.getPromptIndexFromId(group.promptId);
                const backgroundColor = state.getPromptColor(originalIndex);
                const promptText = state.prompts[originalIndex]?.text || '';
                const generationsToShow = firstOnly ? group.generations.slice(0, 1) : group.generations;
                
                return (
                    <div key={`group-${groupIndex}`} className="prompt-output-group" style={{ borderColor: backgroundColor }}>
                        <div className="prompt-output-header"  style={{ backgroundColor: backgroundColor }}>
                            <div className="prompt-text">
                                Prompt {originalIndex + 1}
                                {!urlParams.getBoolean(URLParam.HIDE_PROMPT_TEXT) && (
                                    <>: {promptText}</>
                                )}
                            </div>
                            <div className="prompt-info">
                                {!firstOnly && `${group.generations.length} outputs - `}
                                {state.prompts[originalIndex]?.modelFamily}/{state.prompts[originalIndex]?.model}
                            </div>
                        </div>
                        <div>
                            {generationsToShow.map((generation, index) => (
                                <div 
                                    key={`${generation}-${index}`}
                                    className="output-item"
                                >
                                    {generation}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

    renderOutputsHighlights() {
        const firstGenerations = this.state.promptGroups[0]?.generations;
        return <SingleExampleHighlights 
            generations={firstGenerations}
        ></SingleExampleHighlights>;
    }
  componentDidUpdate(prevProps: any, prevState: any) {
    // If user study mode and current visType is hidden, switch to graph
    if (state.isUserStudy && (state.visType === 'word_tree' || state.visType === 'highlights' || state.visType === 'time_curves')) {
      state.setVisType('graph');
    }

    // Log list load time when raw_outputs/first_output is displayed with real final data (mirrors graph_vis_loaded)
    const isListVis = state.visType === 'raw_outputs' || state.visType === 'first_output';
    const hasRealData = this.state.promptGroups.some(g => g.generations && g.generations.length > 0);
    if (state.isUserStudy && isListVis && hasRealData && !this.hasLoggedListLoadRef) {
      this.hasLoggedListLoadRef = true;
      requestAnimationFrame(() => {
        telemetry.logListVisLoaded();
      });
    }
  }

  componentDidMount() {
    // If user study mode and current visType is hidden, switch to graph
    if (state.isUserStudy && (state.visType === 'word_tree' || state.visType === 'highlights' || state.visType === 'time_curves')) {
      state.setVisType('graph');
    }

    // Log list load time when raw_outputs/first_output is displayed with real final data (mirrors graph_vis_loaded)
    const isListVis = state.visType === 'raw_outputs' || state.visType === 'first_output';
    const hasRealData = this.state.promptGroups.some(g => g.generations && g.generations.length > 0);
    if (state.isUserStudy && isListVis && hasRealData && !this.hasLoggedListLoadRef) {
      this.hasLoggedListLoadRef = true;
      requestAnimationFrame(() => {
        telemetry.logListVisLoaded();
      });
    }

    // react to changes in observable MobX state
    this.disposer = reaction(
      () => {
        const promptTexts = state.prompts.map(p => p.text).join('\u0001');
        const promptTemps = state.prompts.map(p => p.temp).join(',');
        const promptModelFamilies = state.prompts.map(p => p.modelFamily).join(',');
        const promptModels = state.prompts.map(p => p.model).join(',');
        const numGenerations = state.numGenerations;
        const shuffle = state.shuffle;
        const tokenizeMode = state.tokenizeMode;
        const disabledPrompts = state.disabledPrompts;

        return [
          promptTexts,
          promptTemps,
          promptModelFamilies,
          promptModels,
          numGenerations,
          shuffle,
          tokenizeMode,
          disabledPrompts
        ];
      },
      async () => {
        const validPromptsWithIndex = state.prompts
          .map((p, index) => ({ prompt: p, originalIndex: index }))
          .filter(({ prompt, originalIndex }) => 
            prompt.text && prompt.text.trim().length > 0 && !state.isPromptDisabled(originalIndex)
          );
        if (validPromptsWithIndex.length === 0) return;
        const groups: { promptId: string, generations: string[] }[] = [];
        for (let i = 0; i < validPromptsWithIndex.length; i++) {
          const { prompt, originalIndex } = validPromptsWithIndex[i];
          const gens = await state.fetchGenerationsFor(originalIndex);
          groups.push({ promptId: this.getPromptGroupKey(prompt, originalIndex), generations: gens });
        }
        // For the basic outputs view, use the first prompt's generations
        this.setState({promptGroups: groups.length >= 1 ? groups : [] });
      },
      { fireImmediately: true }
    );
  }

  componentWillUnmount() {
    if (this.disposer) this.disposer(); // clean up
  }
}

export default observer(SingleExample);
